import React, { useEffect, useRef } from 'react';
import { Button } from './components/Button';

export interface ConfirmModalProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Dialog heading. */
  title: string;
  /** Body message / question. */
  message: React.ReactNode;
  /** Label of the confirming (primary) button. Default 'Potwierdź'. */
  confirmLabel?: string;
  /** Label of the cancelling (secondary) button. Default 'Anuluj'. */
  cancelLabel?: string;
  /** Use the danger styling on the confirm button (red). Default false. */
  destructive?: boolean;
  /** Confirm button disabled (e.g. until a required acknowledgement is ticked). Default false. */
  confirmDisabled?: boolean;
  /** Called when the user confirms. */
  onConfirm: () => void;
  /** Called on cancel / ESC / scrim click. */
  onCancel: () => void;
}

/**
 * ConfirmModal — drop-in replacement for window.confirm.
 * DS surface panel over a scrim (rgba(20,24,26,.32)), primary + secondary
 * DS Buttons, ESC to cancel, scrim-click to cancel, autofocus on confirm.
 *
 * Self-contained (does not depend on ModalShell) so the confirm flow stays
 * a single small unit; ModalShell is for richer custom dialogs.
 */
/** Warstwa pytania — musi być wyżej niż ModalShell (1300), niżej niż toasty (1400). */
export const CONFIRM_MODAL_Z_INDEX = 1350;

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Potwierdź',
  cancelLabel = 'Anuluj',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Ten sam błąd co w ModalShell (naprawa 2026-09-15): efekt zależał od `onCancel`, a wywołujący
  // podają go inline (nowa funkcja co render) → każdy re-render rodzica oddawał fokus i znów
  // przenosił go na przycisk potwierdzenia. Handler w refie; efekt zależy tylko od `open`.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancelRef.current();
      }
    };
    // `window` + capture, nie `document`: potwierdzenie bywa otwierane NAD otwartym ModalShell, a ten
    // słucha Escape na `document` (capture) i jest zarejestrowany wcześniej — na tym samym węźle
    // `stopPropagation` go nie zatrzyma, więc Esc zamykał CAŁY formularz pod spodem (utrata
    // niezapisanych zmian). Z okna zdarzenie przechwytujemy o poziom wyżej: Esc anuluje tylko pytanie.
    window.addEventListener('keydown', onKeyDown, true);

    // Autofocus the confirm button (last button in the panel).
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[buttons.length - 1]?.focus();

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        // NAD ModalShell (1300): pytanie otwierane z otwartego modala (np. „Zmienić zarządcę?"
        // w „Budynki zarządcy") renderuje się jako rodzeństwo i z 1100 chowało się POD panelem —
        // niewidoczne, a z fokusem na „Potwierdź" (Enter potwierdzał w ciemno). Review 2026-10-02.
        zIndex: CONFIRM_MODAL_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-5)',
        background: 'rgba(20, 24, 26, .32)',
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
        style={{
          width: '100%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)',
          padding: 'var(--space-6)',
          background: 'var(--surface-panel)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-float)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <h2
          id="confirm-modal-title"
          style={{
            margin: 0,
            fontSize: 'var(--text-h2)',
            fontWeight: 'var(--weight-bold)',
            lineHeight: 'var(--leading-tight)',
            color: 'var(--text-heading)',
          }}
        >
          {title}
        </h2>

        <div
          id="confirm-modal-message"
          style={{
            margin: 0,
            fontSize: 'var(--text-body)',
            lineHeight: 'var(--leading-normal)',
            color: 'var(--text-primary)',
          }}
        >
          {message}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
          }}
        >
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={destructive ? { background: 'var(--status-deny-bar)' } : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
